/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';

import styles from './TimePicker.module.css';
import Dropdown from '../../components/controlVariations/DropdownProgram';

interface TimePickerProps {
	value: string;
	onChange: (value: string) => void;

	// Optional minimum allowed time (HH:mm)
	minTime?: string;
	maxTime?: string;
	hasError?: boolean;
}

export const TimePicker: React.FC<TimePickerProps> = ({
	value,
	onChange,
	minTime,
	maxTime,
	hasError = false,
}) => {

	const [shake, setShake] = useState(false);

	const [hours = '00', minutes = '00'] = value.split(':');

	const [minHours = '', minMinutes = ''] = (minTime ?? '').split(':');

	const currentHour = Number(hours);
	const currentMinute = Number(minutes);

	const minimumHour =
		minHours === '' ? null : Number(minHours);

	const minimumMinute =
		minMinutes === '' ? null : Number(minMinutes);

	const [maxHours = '', maxMinutes = ''] =
		(maxTime ?? '').split(':');

	const maximumHour =
		maxHours === '' ? null : Number(maxHours);

	const maximumMinute =
		maxMinutes === '' ? null : Number(maxMinutes);

	const hourOptions = Array.from({ length: 24 }, (_, i) => i)
		.filter(hour => {
			if (minimumHour !== null && hour < minimumHour)
				return false;

			if (maximumHour !== null && hour > maximumHour)
				return false;

			return true;
		})
		.map(hour => ({
			label: String(hour).padStart(2, '0'),
			value: String(hour).padStart(2, '0'),
		}));

	const minuteOptions = Array.from({ length: 60 }, (_, i) => i)
		.filter(minute => {
			if (
				minimumHour !== null &&
				minimumMinute !== null &&
				currentHour === minimumHour &&
				minute <= minimumMinute
			) {
				return false;
			}

			if (
				maximumHour !== null &&
				maximumMinute !== null &&
				currentHour === maximumHour &&
				minute > maximumMinute
			) {
				return false;
			}

			return true;
		})
		.map(minute => ({
			label: String(minute).padStart(2, '0'),
			value: String(minute).padStart(2, '0'),
		}));

	useEffect(() => {
		if (!hasError) return;

		setShake(true);

		const timer = setTimeout(() => {
			setShake(false);
		}, 350);

		return () => clearTimeout(timer);
	}, [hasError]);

	useEffect(() => {
		if (minimumHour === null || minimumMinute === null) return;

		if (
			currentHour < minimumHour ||
			(currentHour === minimumHour &&
				currentMinute <= minimumMinute)
		) {
			const nextMinute =
				minimumMinute === 59 ? 0 : minimumMinute + 1;

			const nextHour =
				minimumMinute === 59
					? minimumHour + 1
					: minimumHour;

			onChange(
				`${String(nextHour).padStart(2, '0')}:${String(
					nextMinute
				).padStart(2, '0')}`
			);
		}
	}, [minTime]);

	return (
		<div
			className={`
		${styles.timeGroup}
		${hasError ? styles.errorBorder : ''}
		${shake ? styles.shake : ''}
	`}
		>
			<div className={styles.timeField}>
				<span className={styles.timeLabel}>HH</span>

				<Dropdown
					placeholder="00"
					options={hourOptions}
					selected={hours}
					onSelect={(value) => {
						let newMinute = minutes;

						if (
							minimumHour !== null &&
							minimumMinute !== null &&
							Number(value) === minimumHour &&
							Number(minutes) <= minimumMinute
						) {
							newMinute = String(minimumMinute + 1).padStart(2, '0');
						}

						onChange(`${value}:${newMinute}`);
					}}
					width="80px"
					height="44px"
				/>
			</div>

			<div className={styles.timeField}>
				<span className={styles.timeLabel}>MM</span>

				<Dropdown
					placeholder="00"
					options={minuteOptions}
					selected={minutes}
					onSelect={(value) =>
						onChange(`${hours}:${value}`)
					}
					width="80px"
					height="44px"
				/>
			</div>
		</div>
	);
};